import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
//--------------------------------------------------------------------------------------------
import UserInfo from "src/components/common/userInfo";
import { testerEmails } from "src/components/common/functions";
import { Organization } from "./types";
import { useAuthStore, User } from "../main/authStore";
//--------------------------------------------------------------------------------------------
type FieldDef = {
    label: string;
    value?: React.ReactNode;
    show?: boolean;
};
//--------------------------------------------------------------------------------------------
export default function OrganizationDetail() {
    const user = useAuthStore(state => state.user) as User;
    const { organization } = useRouteLoaderData<{organization: Organization}>("organization")!;
    const submit = useSubmit();
    const handleDelete = async (id: number) => {
        if(!window.confirm("Are you sure to delete this organization?\n\nThis action cannot be undone.")) return;
        const formData = new FormData();
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Delete"
        });
    };
    const handleValidate = (id:number) => {
        if(!window.confirm("Are you sure to validate this organization?")) return;
        let validateReason = window.prompt("reason to validdate:");
        if(validateReason === null) return;
        while(validateReason === ""){
            window.alert("Reason is required to validate.");
            validateReason = window.prompt("reason to validate:");
            if(validateReason === null) return;
        }
        const formData = new FormData();
        formData.append("validateReason", validateReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Validate"
        });
    }
    const handleVoid = (id: number) => {
        if(!window.confirm("Are you sure to void this organization?")) return;
        let voidReason = window.prompt("reason to void:");
        if(voidReason === null) return;
        while(voidReason === ""){
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if(voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("voidReason", voidReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Void"
        });
    }
    const address = [ organization.street, organization.city, organization.state, organization.zipCode ].filter(Boolean).join(", ");
    const fields: FieldDef[] = [
        { label: "Organization ID", value: organization.id },
        { label: "Name", value: organization.name },
        {
            label: "Website",
            value: organization.website ? (
                <a
                    href = {organization.website}
                    target = "_blank"
                    rel = "noreferrer"
                >
                    {organization.website}
                </a>
            ) : null
        },
        { label: "Business Phone", value: organization.phoneNumber?
            <a href = {`tel:+1${organization.phoneNumber.replace(/-/g, "")}${organization.extension?`,${organization.extension}`:""}`}>
                {organization.phoneNumber}{organization.extension?` Ext. ${organization.extension}`:null}
            </a>
            :<></>        
        },
        { label: "Fax", value: organization.fax?
            <a href = {"fax:+1" + organization.fax.replace(/-/g, "")}>{organization.fax}</a>
            :<></>
        },
        { label: "Address", value: <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target = "_blank" rel="noreferrer">{address}</a>},
        { label: "Oversea Address", value: <a href = {`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(organization.overseaAddress)}`} target = "_blank" rel="noreferrer">{organization.overseaAddress}</a>},
        {
            label: "Creator",
            show: !!organization.creatorId,
            value: (
                <>
                    <UserInfo
                        name={organization.creatorName}
                        id={organization.creatorId}
                    />
                    <span className="ms-2 text-muted">
                        {organization.createdAt}
                    </span>
                </>
            )
        },
        {
            label: "Updater",
            show: !!organization.updaterId,
            value: (
                <>
                    <UserInfo
                        name={organization.updaterName}
                        id={organization.updaterId}
                    />
                    <span className="ms-2 text-muted">
                        {organization.updatedAt}
                    </span>
                </>
            )
        },
        {
            label: "Void Reason",
            show: !!organization.voidReason,
            value: organization.voidReason
        },
        {
            label: "Validation Reason",
            show: !!organization.validateReason,
            value: organization.validateReason
        }
    ];
    // ------------------------------------------------------------------
    return (
        <div>
            <div className="cssSeperateWhenWide ps-3 pt-3">
                <table className="table table-fixed flex-grow-1 maxWidth-50 me-3">
                    <tbody className="noTableTopBorder">
                        {fields
                        .filter(f => f.value && f.show !== false)
                        .map((f, idx) => (
                            <tr key={idx}>
                                <th>{f.label}</th>
                                <td>{f.value}</td>
                            </tr>
                        ))}
                        {organization.contacts.length > 0 && (
                            <tr>
                                <th>Employees</th>
                                <td className="position-relative">
                                    {organization.contacts.slice(0, 5).map(contact => (
                                        <li key={contact.id}>
                                            <Link to={`/Contacts/${contact.id}`}>
                                                {contact.name}
                                            </Link>
                                        </li>
                                    ))}
                                    {organization.contacts.length > 5? 
                                    <li><Link to = {`/Contacts?organizationId=${organization.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                        )}
                        {organization.opportunities.length > 0 && (
                            <tr>
                                <th>Opportunities</th>
                                <td className="position-relative">
                                    {organization.opportunities.slice(0, 5).map(opportunity => (
                                        <li key={opportunity.id}>
                                            <Link to={`/Contacts/${opportunity.id}`}>
                                                {opportunity.name}
                                            </Link>
                                        </li>
                                    ))}
                                    {organization.opportunities.length > 5? 
                                    <li><Link to = {`/Contacts?organizationId=${organization.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                        )}
                        {organization.projects.length > 0 && (
                            <tr>
                                <th>Projects</th>
                                <td>
                                    {organization.projects.slice(0, 5).map(project => (
                                        <li key={project.id}>
                                            <Link to={`/Projects/${project.id}`}>
                                                {project.name}
                                            </Link>
                                        </li>
                                    ))}
                                    {organization.projects.length > 5? 
                                    <li><Link to = {`/Projects?organizationId=${organization.id}`} className="text-warning">More...</Link></li>
                                    :null}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="d-flex flex-column flex-grow-1 maxWidth-50 me-3">
                    <fieldset className="border rounded flex-grow-1 px-3">
                        <legend className="fw-bold">
                            Background
                        </legend>
                        <span className = "cssNotes">
                            {organization.background}
                        </span>
                    </fieldset>
                </div>
            </div>
            <div className="my-3">
            </div>
            <div className="my-3">
                {organization.void === "yes"?<>
                    {testerEmails.includes(user.email)?
                    <button
                        children = "Validate"
                        className = "btn btn-primary"
                        onClick = {() => handleValidate(organization.id)}
                    />:null}
                </>:
                <>
                <Link
                    children = "Add Contact"
                    to = {`/Contacts/Add?organizationId=${organization.id}`}
                    className = "btn btn-outline-info ms-3"
                />
                <Link
                    children = "Edit"
                    to = "Edit"
                    className = "btn btn-outline-primary ms-3"
                />
                <button
                    children = "Void"
                    type = "button"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleVoid(organization.id)}
                />
                </>}
                {organization.void === "yes"?
                <button
                    children = "Delete"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleDelete(organization.id)}
                />:null}
                <Link
                    children = "Back"
                    to = ".."
                    relative = "path"
                    className = "btn btn-outline-secondary float-end"
                />
            </div>
        </div>
    );
}